// DS6.10 for deviations
import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import c from 'classnames';
import { PapiDeviation } from '@wix/da-papi-types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import Flex from '@wix/da-ds/pkg/Flex';
import DeviationCoreBadgeList from '../coreBadge/CoreBadgeList/presets/DeviationCoreBadgeList';
import FaveStat from './FaveStat';
import CommentStat from './CommentStat';
import ViewStat from './ViewStat';
import GroupStat from './GroupStat';
import SubscriberStat from './SubscriberStat';
import PremiumDownloadStat from './PremiumDownloadStat';
import { DeviationTypes } from '../types/deviation';
import { getDeviationType } from '../DeviationViews/_helpers';
import { getCurrentUser, getCurrentUserHasCore } from '../publicSession/selectors';
import DAProtectIconWithTooltip from '../DAProtectIconWithTooltip';
import NoAIWithTooltip from '../NoAIWithTooltip';
import PrivateCollectionStat from './PrivateCollectionsStat/PrivateCollectionsStat';

import s from './Stats.scss';

export interface Props {
  deviation: PapiDeviation;
  /** don't display language after stat count (e.g. comments, views, etc.) */
  onlyNumbers?: boolean;
  /** show the favourite control? (defaults to true) */
  withFavouriteControl?: boolean;
  /** show the comment control? (defaults to true) */
  withCommentControl?: boolean;
  /** show the view count? (defaults to true) */
  withViewCount?: boolean;
  /** show the 'shared to group' count? (defaults to false) */
  withGroupCount?: boolean;
  /** show the private collections count? (defaults to false) */
  withPrivateCollectionsCount?: boolean;
  /** show the subscriber count? (defaults to false) */
  withSubscriberCount?: boolean;
  /** show the premium download count? (defaults to false) */
  withPremiumDownloadCount?: boolean;
  /** show the badge list to the right? (defaults to true) */
  withBadges?: boolean;
  /** show tooltip with list of users that commented or favourited */
  withUserList?: boolean;
  withDaProtectIndicator?: boolean;
  withNoAiIndicator?: boolean;
  className?: string;
  statClassName?: string;
  statWrapperClassName?: string;
  badgelistClassName?: string;
  onCommentClick?: () => void;
  onGroupClick?: () => void;
}

const Stats: React.FC<Props> = ({
  deviation,
  onlyNumbers: showOnlyNumbers,
  withFavouriteControl = true,
  withCommentControl = true,
  withViewCount = true,
  withGroupCount = false,
  withPrivateCollectionsCount = false,
  withSubscriberCount = false,
  withPremiumDownloadCount = false,
  withBadges = true,
  withUserList = true,
  withDaProtectIndicator = true,
  withNoAiIndicator = true,
  className,
  statClassName,
  statWrapperClassName,
  badgelistClassName,
  onCommentClick,
  onGroupClick,
}) => {
  const isMobile = useContext(MobileContext);
  const deviationType = getDeviationType(deviation);
  const onlyNumbers = !!(showOnlyNumbers || isMobile);
  const currentUser = useSelector(getCurrentUser);
  const hasCore = useSelector(getCurrentUserHasCore);
  const isStashItem = deviation?.isPublished === false;

  const isCurrentUserDeviationOwner =
    !!currentUser && currentUser?.userId === deviation.author?.userId;
  const daProtectLevel =
    deviation.extended && deviation.extended.isDaProtected
      ? hasCore
        ? 'active'
        : 'temporary'
      : 'inactive';

  const showDAProtectIndicator =
    withDaProtectIndicator &&
    deviationType === DeviationTypes.IMAGE &&
    isCurrentUserDeviationOwner &&
    !deviation.isDreamsofart;

  const showUserList = !isMobile && withUserList;
  const twoLine = isMobile && isCurrentUserDeviationOwner && withBadges;

  return (
    <Flex
      className={className}
      direction={twoLine ? 'column' : 'row'}
      gap={twoLine ? 4 : 0}
      justifyContent="space-between"
    >
      <Flex className={statWrapperClassName} alignItems="center">
        {withFavouriteControl && !isStashItem && (
          <FaveStat
            className={statClassName}
            deviation={deviation}
            onlyNumbers={onlyNumbers}
            withUserList={showUserList}
          />
        )}
        {withCommentControl && (
          <CommentStat
            className={statClassName}
            deviation={deviation}
            onlyNumbers={onlyNumbers}
            withUserList={showUserList}
            onClick={onCommentClick}
          />
        )}
        {withViewCount && (
          <ViewStat className={statClassName} deviation={deviation} onlyNumbers={onlyNumbers} />
        )}
        {withGroupCount && !isStashItem && (
          <GroupStat
            className={statClassName}
            deviation={deviation}
            onlyNumbers={onlyNumbers}
            onClick={onGroupClick}
          />
        )}
        {withPrivateCollectionsCount && (
          <PrivateCollectionStat
            className={statClassName}
            deviation={deviation}
            onlyNumbers={onlyNumbers}
          />
        )}
        {withSubscriberCount && (
          <SubscriberStat
            className={statClassName}
            deviation={deviation}
            onlyNumbers={onlyNumbers}
          />
        )}
        {withPremiumDownloadCount && (
          <PremiumDownloadStat
            className={statClassName}
            deviation={deviation}
            onlyNumbers={onlyNumbers}
          />
        )}
        {showDAProtectIndicator && !isStashItem && (
          <DAProtectIconWithTooltip
            withLabel
            variant={daProtectLevel}
            className={s['da-protect']}
          />
        )}
        {isCurrentUserDeviationOwner && deviation.isAiUseDisallowed && withNoAiIndicator && (
          <NoAIWithTooltip
            className={c(s['no-ai'], !showDAProtectIndicator && s['no-ai-with-border'])}
          />
        )}
      </Flex>

      {withBadges && (
        <DeviationCoreBadgeList
          deviation={deviation}
          className={c(badgelistClassName, s['badges'], twoLine && s['badges-second-line'])}
        />
      )}
    </Flex>
  );
};

export default Stats;
